import * as Sentry from '@sentry/nextjs'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useTrackedRouter } from '@/lib/attribution/useTrackedRouter'
import { confirmIntentSuccess } from '@/lib/checkout/intent/api'
import type { CheckoutCountry } from '@/lib/checkout/types'
import { useAuthInfo } from '@/lib/hooks/useAuthInfo'
import { useBasketInfo } from '@/lib/hooks/useBasketInfo'

import { PAY_FAILED_MESSAGE } from './constants'

/** The page's own confirmation route, as the session form's return URL, with the reference to confirm. */
export const getIntentConfirmationPath = (
  confirmationPath: string,
  checkoutId: string,
  countryCode: CheckoutCountry,
) => {
  const [path, search = ''] = confirmationPath.split('?')
  const params = new URLSearchParams(search)
  params.set('checkout_id', checkoutId)
  params.set('country_code', countryCode)
  return `${path}?${params.toString()}`
}

export const PAYMENT_RETURN_PARAMS = [
  'redirect_status',
  'payment_intent',
  'payment_intent_client_secret',
  'checkout_id',
] as const

export type PaymentReturn = { status: 'succeeded'; checkoutId: string } | { status: 'failed' }

const firstValue = (value: string | string[] | undefined) => (Array.isArray(value) ? value[0] : value)

/** `processing` still means Stripe took the payment. */
export const readPaymentReturn = (query: Partial<Record<string, string | string[]>>): PaymentReturn | null => {
  const checkoutId = firstValue(query.checkout_id)
  const redirectStatus = firstValue(query.redirect_status)
  if (!checkoutId || !redirectStatus) return null
  return redirectStatus === 'succeeded' || redirectStatus === 'processing'
    ? { status: 'succeeded', checkoutId }
    : { status: 'failed' }
}

interface UsePaymentReturnOptions {
  countryCode: CheckoutCountry
  confirmationPath: string
}

/**
 * A Klarna return lands on the checkout: a paid one tells payment-service the next action succeeded and goes on to the
 * confirmation page; a failed one starts a fresh reference (the old one is never resumed) and drops Stripe's
 * parameters so a reload does not repeat the notice.
 */
export const usePaymentReturn = ({ countryCode, confirmationPath }: UsePaymentReturnOptions) => {
  const { authenticatedUser } = useAuthInfo()
  // The page's cross-sell, and so `confirmationPath`, is read from the basket.
  const { isBasketInitialised } = useBasketInfo()
  const router = useTrackedRouter()
  const [paymentReturn] = useState(() => readPaymentReturn(router.query))
  const [notice, setNotice] = useState(paymentReturn?.status === 'failed' ? PAY_FAILED_MESSAGE : null)
  const handledRef = useRef(false)

  useEffect(() => {
    if (!paymentReturn || handledRef.current) return
    if (paymentReturn.status === 'succeeded') {
      if (!isBasketInitialised || !authenticatedUser) return
      handledRef.current = true
      const forward = async () => {
        try {
          await confirmIntentSuccess(authenticatedUser, paymentReturn.checkoutId)
        } catch (error) {
          // The payment may still have gone through, so the confirmation page decides what the customer sees.
          Sentry.captureException(error, { extra: { firebaseUID: authenticatedUser.uid } })
        }
        void router.replace(getIntentConfirmationPath(confirmationPath, paymentReturn.checkoutId, countryCode))
      }
      void forward()
      return
    }
    handledRef.current = true
    const [path, search = ''] = router.asPath.split('#')[0].split('?')
    const params = new URLSearchParams(search)
    PAYMENT_RETURN_PARAMS.forEach((param) => params.delete(param))
    const query = params.toString()
    void router.replace(query ? `${path}?${query}` : path, undefined, { shallow: true })
  }, [paymentReturn, isBasketInitialised, authenticatedUser, router, confirmationPath, countryCode])

  const clearNotice = useCallback(() => setNotice(null), [])

  return { isPaidReturn: paymentReturn?.status === 'succeeded', notice, clearNotice }
}
